import MatchReport from '../modules/match-report/MatchReport';
import {
    CreateEventCommand,
    CreateMatchPlayerCommandOfMatchPlayer,
    EventPeriodEnum,
    EventTypeEnum,
    EventVm,
    ExclusionRequest,
    ExclusionTypeEnum,
    MatchDetailVm,
    ShotRequest,
    ShotTypeEnum,
    TimeoutRequest,
} from '../utils/api';
import { splitWholeSeconds } from './countdownClock';

export interface MobileEventTiming {
    periodId: EventPeriodEnum;
    /** Remaining seconds on the period clock (counts down 8:00 -> 0:00). */
    timeRemaining: number;
}

/**
 * Active goalkeeper match-player ids at the given point in time, reusing the
 * same derivation the desktop match report uses (lineup GK + GK change events).
 * The backend needs these so missed shots get auto `savedById`.
 */
export const getActiveGoalkeeperMatchPlayerIds = (
    match: MatchDetailVm,
    events: EventVm[],
    timing: MobileEventTiming
): { homeGkId?: number; awayGkId?: number } => {
    const lineups = (match.players as CreateMatchPlayerCommandOfMatchPlayer[]) || [];
    const { homeKeeper, awayKeeper } = MatchReport.deriveActiveGoalkeepers(
        match,
        events,
        timing.periodId,
        timing.timeRemaining
    );
    const homeGk = lineups.find((l) => l.teamId === match.homeTeamId && l.number === homeKeeper);
    const awayGk = lineups.find((l) => l.teamId === match.awayTeamId && l.number === awayKeeper);
    return { homeGkId: homeGk?.id, awayGkId: awayGk?.id };
};

const splitTime = (timeRemaining: number): { minute: number; seconds: number } =>
    splitWholeSeconds(timeRemaining);

const baseCommand = (
    match: MatchDetailVm,
    events: EventVm[],
    typeId: EventTypeEnum,
    timing: MobileEventTiming
): CreateEventCommand => {
    const { minute, seconds } = splitTime(timing.timeRemaining);
    const { homeGkId, awayGkId } = getActiveGoalkeeperMatchPlayerIds(match, events, timing);
    return new CreateEventCommand({
        matchId: match.id,
        typeId,
        periodId: timing.periodId,
        minute,
        seconds,
        activeHomeGoalkeeperMatchPlayerId: homeGkId,
        activeAwayGoalkeeperMatchPlayerId: awayGkId,
    } as any);
};

/**
 * Goal or missed shot. Mobile records only the essentials (team, shooter,
 * regular/penalty, goal flag); no pool location/target. Backend assigns
 * `savedById` from the active goalkeeper on a miss.
 */
export const buildShotCommand = (
    match: MatchDetailVm,
    events: EventVm[],
    timing: MobileEventTiming,
    args: {
        teamId: number;
        takenById: number;
        isGoal: boolean;
        isPenalty: boolean;
    }
): CreateEventCommand => {
    const command = baseCommand(match, events, EventTypeEnum.Shot, timing);
    command.shot = new ShotRequest({
        id: 0,
        teamId: args.teamId,
        typeId: args.isPenalty ? ShotTypeEnum.Penalty : ShotTypeEnum.Regular_Attack,
        takenById: args.takenById,
        blockedById: undefined,
        assistedById: undefined,
        savedById: undefined,
        isGoal: args.isGoal,
        isFastBreak: false,
        isDirectFromFoul: false,
        shotTechnique: undefined,
        fakeCount: 0,
        locationX: undefined,
        locationY: undefined,
        targetX: undefined,
        targetY: undefined,
    });
    return command;
};

/** Field-player exclusion (exclusion / suspension / brutality / penalty). No pool location. */
export const buildExclusionCommand = (
    match: MatchDetailVm,
    events: EventVm[],
    timing: MobileEventTiming,
    args: {
        teamId: number;
        excludedPlayerId: number;
        typeId: ExclusionTypeEnum;
    }
): CreateEventCommand => {
    const command = baseCommand(match, events, EventTypeEnum.Exclusion, timing);
    command.exclusion = new ExclusionRequest({
        id: 0,
        teamId: args.teamId,
        typeId: args.typeId,
        excludedPlayerId: args.excludedPlayerId,
        excludedCoachId: undefined,
        fouledPlayerId: undefined,
        assistedById: undefined,
        isPenaltyExclusion: args.typeId === ExclusionTypeEnum.Penalty,
        isDoubleExclusion: false,
        locationX: undefined,
        locationY: undefined,
    });
    return command;
};

export const buildTimeoutCommand = (
    match: MatchDetailVm,
    events: EventVm[],
    timing: MobileEventTiming,
    teamId: number
): CreateEventCommand => {
    const command = baseCommand(match, events, EventTypeEnum.Timeout, timing);
    command.timeout = new TimeoutRequest({ id: 0, teamId });
    return command;
};
